import LinkPageItemIcon from "./LinkPageItemIcon";
import { CalendarOutlined } from "@ant-design/icons";
import { Empty } from "antd";
import dynamic from "next/dynamic";
import { getLinkPageVideo } from "../../utils/linkPageMedia";
import FontStyles from "../FontStyles";

import { blockBooking, bookingSummary, hasPublicBookingServices } from "../../utils/bookingAvailability";
import UserIdentityCard from "../UserIdentity/UserIdentityCard";
import {
  ArticlesBlock,
  CoverMedia,
  EventsBlock,
  ReactionsBlock,
  RowLink,
  TickerBlock,
} from "./LinkPageContentBlocks";
import {
  DEFAULT_LINK_PAGE,
  sectionColumns,
  getItemDescription,
  getItemHref,
  getRenderableItems,
  getThemeStyle,
  linkPageBio,
  linkPageHeading,
  normalizeLinkPageVersion,
} from "./linkPageBuilderUtils";

const NO_REACTIONS = {};
const NO_ARTICLES = [];
const BookMeBlock = dynamic(() => import("../Booking/BookMeBlock"));

const getAlignmentClass = (alignment) =>
  alignment === "left"
    ? "linkPageRenderer__stack--left"
    : "linkPageRenderer__stack--center";

const getButtonStyleClass = (style) => {
  switch (style) {
    case "outline":
      return "linkPageRenderer__card--outline";
    case "soft":
      return "linkPageRenderer__card--soft";
    case "glass":
      return "linkPageRenderer__card--glass";
    default:
      return "linkPageRenderer__card--solid";
  }
};

const getButtonSizeClass = (size) => {
  switch (size) {
    case "compact":
      return "linkPageRenderer__card--compact";
    case "large":
      return "linkPageRenderer__card--large";
    default:
      return "linkPageRenderer__card--comfortable";
  }
};

const getCardStyle = (item) => {
  const style = {};

  if (item.buttonBackgroundColor) {
    style["--lp-item-background"] = item.buttonBackgroundColor;
  }

  if (item.buttonTextColor) {
    style["--lp-item-text"] = item.buttonTextColor;
  }

  return style;
};

export default function LinkPageRenderer({
  profileInfo,
  version,
  showEmptyState = false,
  device = "auto",
  disableLinks = false,
  selectedItemId = null,
  onSelectItem,
  bookingPreview,
  onItemClick,
  autoOpenBooking,
  selectedSectionId = null,
  onSelectSection,
  pageId,
  reactions = NO_REACTIONS,
  articles = NO_ARTICLES,
}) {
  const normalizedVersion = normalizeLinkPageVersion(
    version || DEFAULT_LINK_PAGE.published
  );
  const items = getRenderableItems(normalizedVersion);
  const themeStyle = getThemeStyle(normalizedVersion.theme);
  const channelTitle = linkPageHeading(normalizedVersion, profileInfo);
  const bio = linkPageBio(normalizedVersion, profileInfo);
  const fullWidth = (item) =>
    normalizedVersion.sections.find((section) => section.id === item.sectionId)?.layout === "full";
  const renderItem = (item) => {
                const select = () => onSelectItem?.(item.id);
                if (item.type === "ticker") return <TickerBlock key={item.id} item={item} />;
                if (item.type === "events")
                  return (
                    <EventsBlock
                      key={item.id}
                      item={item}
                      disableLinks={disableLinks}
                      onSelect={select}
                      onClick={() => onItemClick?.(item)}
                    />
                  );
                if (item.type === "articles")
                  return (
                    <ArticlesBlock
                      key={item.id}
                      item={item}
                      articles={articles}
                      disableLinks={disableLinks}
                      onSelect={select}
                      onClick={() => onItemClick?.(item)}
                    />
                  );
                if (item.type === "reactions")
                  return (
                    <ReactionsBlock
                      key={item.id}
                      item={item}
                      counts={reactions[item.id]}
                      pageId={pageId}
                      disableLinks={disableLinks}
                      onSelect={select}
                    />
                  );
                if (item.type === "image" && fullWidth(item))
                  return (
                    <div key={item.id} className="lpBand">
                      <CoverMedia item={item} onSelect={select}>
                        {item.subtitle ? <p className="lpBand__quote">{item.subtitle}</p> : null}
                      </CoverMedia>
                    </div>
                  );
                if (item.type === "text") {
                  return (
                    <div key={item.id} className="linkPageRenderer__section" onClick={select}>
                      {item.title ? <strong className="linkPageRenderer__sectionTitle lpEyebrow">
                        {item.title}
                      </strong> : null}
                      {item.subtitle ? (
                        <span className="linkPageRenderer__sectionSubtitle">
                          {item.subtitle}
                        </span>
                      ) : null}
                    </div>
                  );
                }
                if (item.type === "booking") {
                  const booking = blockBooking(profileInfo?.bookingProfile, item.value, item.variant);
                  return hasPublicBookingServices(booking) ? (
                    <BookMeBlock
                      key={item.id}
                      sellerId={profileInfo._id}
                      bookingProfile={booking}
                      title={item.title}
                      triggerLabel={item.title}
                      triggerMeta={bookingSummary(booking).meta}
                      themeStyle={themeStyle}
                      preview={disableLinks}
                      autoOpen={autoOpenBooking === item.id}
                      onOpen={() => {
                        onSelectItem?.(item.id);
                        if (!disableLinks) onItemClick?.(item);
                      }}
                      triggerClassName={`linkPageRenderer__card ${getButtonStyleClass(
                        normalizedVersion.theme.buttonStyle
                      )} ${getButtonSizeClass(
                        normalizedVersion.theme.buttonSize
                      )}`}
                    />
                  ) : disableLinks ? (
                    <button
                      key={item.id}
                      type="button"
                      className={`linkPageRenderer__card ${getButtonStyleClass(normalizedVersion.theme.buttonStyle)} ${getButtonSizeClass(normalizedVersion.theme.buttonSize)}`}
                      onClick={() => onSelectItem?.(item.id)}
                    >
                      <span className="linkPageRenderer__cardIcon"><CalendarOutlined /></span>
                      <span className="linkPageRenderer__cardContent">
                        <strong className="linkPageRenderer__cardTitle">{item.title || "Book Me"}</strong>
                        <span className="linkPageRenderer__cardSubtitle">Set up Book Me to accept bookings</span>
                      </span>
                    </button>
                  ) : null;
                }
                if (item.type === "socialRow")
                  return (
                    <div key={item.id} className="linkPageRenderer__socialRow"
                      style={{ "--lp-social-count": Math.max(1, item.links?.length || 0) }}>
                      {(item.links || []).map((link, index) => (
                        <a
                          key={link.id || index}
                          aria-label={
                            link.title || link.platform || "Social link"
                          }
                          href={
                            disableLinks
                              ? undefined
                              : getItemHref({ ...link, type: "social" })
                          }
                          target={disableLinks ? undefined : "_blank"}
                          rel="noopener noreferrer"
                          onClick={(event) => {
                            if (disableLinks) event.preventDefault();
                            onSelectItem?.(item.id);
                            if (!disableLinks) onItemClick?.(item);
                          }}
                        >
                          <LinkPageItemIcon
                            item={{ ...link, type: "social" }}
                          />
                        </a>
                      ))}
                    </div>
                  );
                if (item.type === "image")
                  return (
                    <figure
                      key={item.id}
                      className="linkPageRenderer__image"
                      onClick={() => onSelectItem?.(item.id)}
                    >
                      {item.url ? (
                        <img
                          src={item.url}
                          alt={item.title || ""}
                          loading="lazy"
                        />
                      ) : null}
                      {item.subtitle ? (
                        <figcaption>{item.subtitle}</figcaption>
                      ) : null}
                    </figure>
                  );
                if (item.type === "video") {
                  const video = getLinkPageVideo(item.url);
                  return (
                    <div key={item.id} className="linkPageRenderer__video">
                      {video ? <iframe
                        src={`${video.embedUrl}?playsinline=1`}
                        title={item.title || "YouTube video"}
                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      /> : null}

                    </div>
                  );
                }

                const href = getItemHref(item);
                const description = getItemDescription(item);
                const isEmailContact =
                  item.type === "contact" &&
                  item.platform === "email" &&
                  Boolean(item.value?.trim?.());
                const displayTitle = isEmailContact
                  ? item.value.trim()
                  : item.title || "Untitled item";
                const displayDescription = isEmailContact ? "" : description;
                const isSelected = selectedItemId === item.id;
                const handleSelect = (event) => {
                  if (disableLinks) {
                    event.preventDefault();
                  }
                  onSelectItem?.(item.id);
                  if (!disableLinks) onItemClick?.(item);
                };

                if (item.variant === "row")
                  return (
                    <RowLink
                      key={item.id}
                      href={href}
                      title={displayTitle}
                      description={displayDescription}
                      disableLinks={disableLinks}
                      selected={isSelected}
                      onClick={handleSelect}
                    />
                  );
                return (
                  <a
                    key={item.id}
                    href={href || undefined}
                    tabIndex={disableLinks ? -1 : undefined}
                    target={href && !disableLinks ? "_blank" : undefined}
                    rel={
                      href && !disableLinks ? "noopener noreferrer" : undefined
                    }
                    className={`linkPageRenderer__card ${
                      item.variant === "card"
                        ? "linkPageRenderer__card--cover"
                        : ""
                    } ${getButtonStyleClass(
                      normalizedVersion.theme.buttonStyle
                    )} ${getButtonSizeClass(
                      normalizedVersion.theme.buttonSize
                    )} ${
                      isSelected ? "linkPageRenderer__card--selected" : ""
                    } ${disableLinks ? "linkPageRenderer__card--editor" : ""}`}
                    style={getCardStyle(item)}
                    onClick={handleSelect}
                  >
                    {item.thumbnail ? (
                      <img
                        className="linkPageRenderer__thumbnail"
                        src={item.thumbnail}
                        alt=""
                        loading="lazy"
                      />
                    ) : (
                      <span className="linkPageRenderer__cardIcon">
                        <LinkPageItemIcon item={item} />
                      </span>
                    )}
                    <span className="linkPageRenderer__cardContent">
                      <strong className="linkPageRenderer__cardTitle">
                        {displayTitle}
                      </strong>
                      {displayDescription ? (
                        <span className="linkPageRenderer__cardSubtitle">
                          {displayDescription}
                        </span>
                      ) : null}
                    </span>
                  </a>
                );
  };
  const sections = normalizedVersion.sections.filter((section) => section.isVisible !== false);
  const heroSection = sections.find((section) => section.showProfile);
  const profileCard = (
    <UserIdentityCard
      className="linkPageRenderer__profileCard"
      profileHref={disableLinks ? "" : undefined}
      showAvatar={false}
      size={88}
      subtitle={
        <span className="linkPageRenderer__profileMeta">
          {profileInfo?.nickname ? (
            <span className="linkPageRenderer__handle">@{profileInfo.nickname}</span>
          ) : null}
          {bio ? <span className="linkPageRenderer__bio">{bio}</span> : null}
        </span>
      }
      title={channelTitle || "Your page"}
      user={profileInfo}
      variant="profile"
    />
  );
  const sectionItems = (section, column) =>
    items.filter((item) => item.sectionId === section.id && (column === undefined || item.column === column));
  const renderHero = (section) => {
    const own = sectionItems(section);
    const media = own.find((item) => ["video", "image"].includes(item.type));
    const socials = own.filter((item) => item.type === "socialRow");
    const rest = own.filter((item) => item !== media && !socials.includes(item));
    return (
      <>
        <CoverMedia item={media} onSelect={() => media && onSelectItem?.(media.id)}>
          <div className="lpHero__identity">
            {media?.title ? <div className="lpEyebrow">{media.title}</div> : null}
            <h1 className="lpHero__name">{channelTitle || "Your page"}</h1>
          </div>
          <div className="lpHero__aside">
            {bio ? <p>{bio}</p> : null}
            {socials.map(renderItem)}
          </div>
        </CoverMedia>
        {rest.length ? <div className="lpSection__boxed">{rest.map(renderItem)}</div> : null}
      </>
    );
  };
  const renderSection = (section) => {
    const columns = Array.from({ length: sectionColumns(section) }, (_, index) => sectionItems(section, index));
    if (!section.showProfile && !columns.some((column) => column.length)) return null;
    return (
      <section
        key={section.id}
        data-section-id={section.id}
        className={`lpSection lpSection--${section.layout} ${section.showProfile ? "lpSection--hero" : ""} ${
          selectedSectionId === section.id ? "lpSection--selected" : ""
        }`}
        onClickCapture={() => onSelectSection?.(section.id)}
      >
        {section.showProfile ? (
          renderHero(section)
        ) : (
          <div className="lpSection__grid">
            {columns.map((column, index) => (
              <div key={index} className="lpSection__column">
                {column.map(renderItem)}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };
  return (
    <div
      className={`linkPageRenderer linkPageRenderer--${device}`}
      style={themeStyle}
    >
      <FontStyles families={[normalizedVersion.theme.fontFamily]} />
      <div className="linkPageRenderer__phone">
        <div
          className={`linkPageRenderer__stack ${getAlignmentClass(
            normalizedVersion.layout.alignment
          )}`}
        >
          {heroSection ? null : profileCard}

          <div className="linkPageRenderer__items">
            {normalizedVersion.bookingMode !== "blocks" &&
            !items.some((item) => item.type === "booking") &&
            hasPublicBookingServices(profileInfo?.bookingProfile)
              ? <div className="linkPageRenderer__autoBooking">
                {bookingPreview || (
                  <BookMeBlock
                    key={profileInfo?._id}
                    sellerId={profileInfo?._id}
                    bookingProfile={profileInfo.bookingProfile}
                    triggerMeta={bookingSummary(profileInfo.bookingProfile).meta}
                    themeStyle={themeStyle}
                    preview={disableLinks}
                    triggerClassName={`linkPageRenderer__card ${getButtonStyleClass(
                      normalizedVersion.theme.buttonStyle
                    )} ${getButtonSizeClass(
                      normalizedVersion.theme.buttonSize
                    )}`}
                  />
                )}
                </div>
              : null}

            {items.length > 0 || heroSection ? (
              sections.map(renderSection)
            ) : showEmptyState ? (
              <div className="linkPageRenderer__empty">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No published links yet"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
